import { useEffect, useState, useCallback } from 'react';

export interface ReportProgressUpdate {
    report_id: number;
    status: 'queued' | 'executing_sql' | 'processing_data' | 'generating_report' | 'downloading' | 'completed' | 'error';
    message: string;
    percentage: number;
    step: number;
    total_steps: number;
    error?: string;
    timestamp?: string;
}

export interface ReportProgressState {
    isLoading: boolean;
    currentStatus: string;
    percentage: number;
    currentStep: number;
    totalSteps: number;
    error: string | null;
    isConnected: boolean;
}

/**
 * Hook to track report generation progress via existing WebSocket connection
 * 
 * The progress updates come through the main WebSocket connection that's
 * already used for other real-time features. No separate connection needed.
 * 
 * Usage:
 * ```tsx
 * const { progressState } = useReportProgress(reportId);
 * 
 * return (
 *   <Box>
 *     <Text>{progressState.currentStatus}</Text>
 *     <ProgressBar value={progressState.percentage} />
 *   </Box>
 * );
 * ```
 */
export const useReportProgress = (reportId: number | null) => {
    const [progressState, setProgressState] = useState<ReportProgressState>({
        isLoading: false,
        currentStatus: 'Waiting...',
        percentage: 0,
        currentStep: 0,
        totalSteps: 5,
        error: null,
        isConnected: true  // We assume main WS is already connected
    });

    // Listen for progress updates from WebSocket
    useEffect(() => {
        if (!reportId) return;

        const handleMessage = (event: Event) => {
            try {
                const messageEvent = event as any;
                const data = messageEvent.detail || JSON.parse(messageEvent.data);

                // Check if this is a progress update for this report
                if (data.report_id === reportId) {
                    setProgressState(prev => ({
                        ...prev,
                        currentStatus: data.message,
                        percentage: data.percentage,
                        currentStep: data.step,
                        totalSteps: data.total_steps || 5,
                        error: data.error || null,
                        isLoading: data.status !== 'completed' && data.status !== 'error'
                    }));
                }
            } catch (error) {
                // Silently ignore parsing errors (not all messages are progress updates)
            }
        };

        // Listen for progress updates dispatched from WebSocket handler
        window.addEventListener('report-progress', handleMessage);

        return () => {
            window.removeEventListener('report-progress', handleMessage);
        };
    }, [reportId]);

    const reset = useCallback(() => {
        setProgressState({
            isLoading: false,
            currentStatus: 'Waiting...',
            percentage: 0,
            currentStep: 0,
            totalSteps: 5,
            error: null,
            isConnected: true
        });
    }, []);

    // Dummy connectWebSocket function - not needed anymore since we use main WS
    const connectWebSocket = useCallback(() => {
        // No-op: progress updates come through main WebSocket
    }, []);

    return {
        progressState,
        connectWebSocket,
        reset
    };
};
